import { useEffect, useRef, useState } from 'react';
import { AlertTriangle, Check, Coins, Eye, Link2, Lock, Mail, MessageSquare, PenLine, Pencil, RotateCcw, Scale, Settings, User } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { useAuth, hasRole } from '../lib/auth';
import { useI18n } from '../lib/i18n';
import { copiarAlPortapapeles } from '../lib/portapapeles';
import { uploadFile } from '../lib/upload';
import { FileDrop } from './FileDrop';
import { SignaturePad, type SignaturePadHandle } from './SignaturePad';
import { ContractEditModal } from './ContractEditModal';

type Contract = {
  id: string; status: string; state: string | null;
  sent_to_email: string | null; sent_at: string | null; opened_at: string | null;
  homeowner_signed_at: string | null; pa_signed_at: string | null; pa_stamped?: boolean; created_at: string;
  filled_url: string | null; signed_url: string | null; signing_url: string | null;
  fee_percent?: number | null; pa_name?: string | null;
  /** ¿YO soy el PA impreso? Lo calcula el backend con la misma regla que aplica al firmar. */
  can_pa_sign?: boolean;
  sends?: Array<{ at: string; to: string | null; via?: 'email' | 'sms' }>;
};

const fmt = (s: string | null) => (s ? new Date(s).toLocaleString('en-US') : null);

/** Panel del contrato en la sección Intake del claim: estado + tracking + acciones.
 * kind='pa' (Public Adjuster, el de siempre — lo firma el homeowner y luego el PA) o
 * kind='appraiser' (uno global, lo firma SOLO el homeowner, no mueve el status del claim). */
export function ContractPanel({ claimId, onChanged, kind = 'pa' }: { claimId: string; onChanged?: () => void; kind?: 'pa' | 'appraiser' }) {
  const { t } = useI18n();
  const isAppraiser = kind === 'appraiser';
  const { me } = useAuth();
  const canManage = hasRole(me, 'office'); // Intake+: preparar / subir firmado / enviar e-sign
  // FIRMA: solo el PA IMPRESO en el documento. Desde 2026-07-17 el botón era para todos los
  // office+ "porque el candado de identidad está en el backend" — no estaba: cualquiera podía
  // estampar su firma sobre el nombre de otro, y así salió el contrato de Thomas Chacko (impreso
  // Manuel, firmado Jessika). Ahora el candado existe de verdad en paSign y `can_pa_sign` lo
  // espeja acá para no ofrecer un botón que el servidor va a rechazar.
  const [contract, setContract] = useState<Contract | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [paErr, setPaErr] = useState<string | null>(null);
  const [paOpen, setPaOpen] = useState(false);
  // Editor de los datos ya estampados en el contrato (+ historial de correcciones).
  const [editOpen, setEditOpen] = useState(false);
  const padRef = useRef<SignaturePadHandle>(null);

  const qk = isAppraiser ? '?kind=appraiser' : '';
  async function load() {
    try { setContract((await api.get<{ contract: Contract | null }>(`/api/claims/${claimId}/contract${qk}`)).contract); }
    catch { /* ignore */ } finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [claimId, kind]);

  // Códigos del backend → mensajes que el equipo entiende (los crudos confundían).
  const ERR_MAP: Record<string, string> = {
    homeowner_must_sign_first: 'The homeowner must sign first · El homeowner debe firmar primero.',
    pa_already_signed: 'The PA signature is already stamped on this contract · La firma del PA ya está estampada.',
    signature_invalid: 'We could not read the signature — try again or use Type · No se pudo leer la firma, reintenta o usa Escribir.',
    homeowner_already_signed: 'The homeowner already signed — regenerating would destroy their signature · El homeowner ya firmó; regenerar destruiría su firma.',
    contract_already_signed: 'This claim already has a fully signed contract · Este claim ya tiene un contrato firmado.',
    no_appraiser_template: 'No appraiser template uploaded yet — add one in Settings → Contracts · Aún no hay plantilla de appraiser; súbela en Settings → Contracts.',
    no_homeowner_phone: t('cx.errNoPhone'),
    sms_failed: t('cx.errSmsFailed'),
    no_homeowner_email: 'The homeowner has no email — add it in the Client card and send again · El homeowner no tiene email; agrégalo en la tarjeta del cliente y vuelve a enviar.',
    // El documento dice un nombre y lo iba a firmar otro: eso NO se sella.
    signer_not_contract_pa: t('cx.errSignerNotPa'),
    contract_without_pa: t('cx.errNoPaOnContract'),
    auto_sign_requires_typed: t('cx.errAutoSignTyped'),
  };
  // El estado del contrato viene con prefijo (team_pa_not_licensed_for_state_FL): se traduce por
  // prefijo para no tener 50 entradas, una por estado. `state_mismatch_PA_GA` trae LOS DOS: el
  // del form y el de la dirección, en ese orden.
  const errText = (code: string) =>
    (code.startsWith('team_pa_not_licensed_for_state_') ? t('cx.errTeamPaNotLicensed', { state: code.slice(-2) })
      : code.startsWith('no_pa_licensed_for_state_') ? t('ct.paMissingWarn', { state: code.slice(-2) })
        : code.startsWith('state_mismatch_') ? t('ct.stateMismatchWarn', { form: code.slice(-5, -3), address: code.slice(-2) })
          : ERR_MAP[code]) ?? code;
  async function run(label: string, fn: () => Promise<unknown>) {
    setBusy(label); setMsg(null);
    try { await fn(); await load(); onChanged?.(); }
    catch (e) { setMsg(e instanceof ApiError ? errText(e.message) : 'Error'); } finally { setBusy(null); }
  }
  const prepBody = isAppraiser ? { kind: 'appraiser' } : {};
  const prepare = () => run('prepare', () => api.post(`/api/claims/${claimId}/contract/prepare`, prepBody).then((c) => setContract(c as Contract)));
  const send = () => run('send', () => api.post(`/api/contracts/${contract!.id}/send`, {}).then(() => setMsg(t('cx.sentOk'))));
  // Link de firma por SMS (Manuel 2026-09-14): texto en inglés con el secure link, al teléfono de la tarjeta.
  const sendSms = () => run('sms', () => api.post(`/api/contracts/${contract!.id}/send-sms`, {}).then(() => setMsg(t('cx.smsSentOk'))));
  // e-sign en un paso desde el estado vacío: prepara el contrato y lo envía a firmar.
  const prepareAndSend = () => run('esign', async () => {
    const c = await api.post<Contract>(`/api/claims/${claimId}/contract/prepare`, prepBody);
    await api.post(`/api/contracts/${c.id}/send`, {});
    setMsg(t('cx.sentOk'));
  });
  async function paSign() {
    const data = padRef.current?.toDataURL();
    if (!data) { setPaErr(t('cx.drawFirst')); return; }
    // NO usa run(): el modal solo se cierra si la firma DE VERDAD quedó estampada.
    // (Antes un error del servidor cerraba el modal en silencio → "no se hace efectivo".)
    setBusy('pasign'); setPaErr(null); setMsg(null);
    try {
      await api.post(`/api/contracts/${contract!.id}/pa-sign`, { signature: data });
      await load(); onChanged?.();
      setPaOpen(false); setMsg(t('cx.paSignedOk'));
    } catch (e) {
      setPaErr(e instanceof ApiError ? errText(e.message) : 'Error');
    } finally { setBusy(null); }
  }
  async function uploadSigned(file: File) {
    await run('paper', async () => { const key = await uploadFile(file, 'misc'); await api.post(`/api/claims/${claimId}/contract/upload-signed`, { fileKey: key }); setMsg(t('cx.paperUploaded')); });
  }
  // "Link copiado" SOLO si se copió: el link de firma es lo que se le pega al homeowner por WhatsApp,
  // y decirle que está en el portapapeles cuando no está es peor que no decir nada.
  async function copiarLinkDeFirma(url: string) {
    if (await copiarAlPortapapeles(url)) setMsg(t('cx.linkCopied'));
  }

  if (loading) return <div className="muted" style={{ fontSize: 'var(--fs-sm)' }}>…</div>;

  const c = contract;
  // Historial de envíos (original + reenvíos). Fallback a sent_at para contratos viejos.
  const sends = c?.sends && c.sends.length ? c.sends : (c?.sent_at ? [{ at: c.sent_at, to: c.sent_to_email }] : []);
  // Paso del checklist estilo JobFlow: círculo VERDE con check (hecho) / círculo vacío (pendiente),
  // label a la izquierda y la fecha a la DERECHA.
  const Dot = ({ done }: { done: boolean }) => (
    <span style={{ width: 18, height: 18, borderRadius: '50%', flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      background: done ? 'var(--green)' : 'transparent', border: done ? 'none' : '2px solid var(--line)', color: '#fff', fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)' }}>
      {done ? <Check size={12} strokeWidth={2.5} /> : ''}
    </span>
  );
  const step = (label: string, at: string | null, done: boolean) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 'var(--fs-sm)' }}>
      <Dot done={done} />
      <span style={{ fontWeight: done ? 'var(--fw-medium)' : 'var(--fw-body)', flex: 1, color: done ? 'var(--ink)' : 'var(--muted)' }}>{label}</span>
      {at && <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{fmt(at)}</span>}
    </div>
  );

  return (
    <div className="card" style={{ maxWidth: 620 }}>
      {/* Título "Public Adjuster Contract" + PA y Fee% debajo + "View contract ↗" a la derecha. */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 10 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-semibold)', color: 'var(--ink)' }}>{isAppraiser ? <><Scale size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('cx.appraiserTitle')}</> : t('cx.paTitle')}</div>
          {/* PA del contrato + Fee% (pedido): se ven aunque el contrato aún no exista si vienen del row.
              El appraiser no tiene PA ni fee → no se muestran. */}
          {!isAppraiser && c && (c.pa_name || c.fee_percent != null) && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
              {c.pa_name && <span className="badge" style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)', background: 'var(--accent-soft)', color: 'var(--accent)' }}><User size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {c.pa_name}</span>}
              {c.fee_percent != null && <span className="badge" style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)', background: 'var(--accent-soft)', color: 'var(--accent)' }}><Coins size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('cx.fee')}: {c.fee_percent}%</span>}
            </div>
          )}
        </div>
        {c && (c.signed_url || c.filled_url) && (
          <a href={(c.signed_url || c.filled_url)!} target="_blank" rel="noopener noreferrer" style={{ fontSize: 'var(--fs-xs)', flexShrink: 0 }}>{t('cx.view')} ↗</a>
        )}
      </div>
      {msg && <div className="muted" style={{ fontSize: 'var(--fs-2xs)', marginBottom: 8 }}>{msg}</div>}

      {!c ? (
        <div style={{ display: 'grid', gap: 10 }}>
          <div className="muted" style={{ fontSize: 'var(--fs-sm)' }}>{isAppraiser ? t('cx.appraiserNone') : t('cx.none')}</div>
          {canManage && (
            <>
              {/* Opción A — mandar a firmar por e-sign (prepara + envía en un paso). */}
              <div style={{ display: 'grid', gap: 4 }}>
                <div className="muted" style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-semibold)', textTransform: 'uppercase', letterSpacing: 0.3 }}>{t('cx.esignSection')}</div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button className="btn sm" disabled={!!busy} onClick={prepareAndSend} style={{ background: 'var(--info)', color: '#fff', borderColor: 'transparent' }}><Mail size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {busy === 'esign' ? '…' : t('cx.sendEsign')}</button>
                  <button className="btn ghost sm" disabled={!!busy} onClick={prepare} title={t('cx.prepareHint')}><Settings size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {busy === 'prepare' ? '…' : t('cx.prepare')}</button>
                </div>
              </div>
              {/* Opción B — subir el contrato ya firmado (papel). */}
              <div style={{ display: 'grid', gap: 4, borderTop: '1px dashed var(--line)', paddingTop: 8 }}>
                <div className="muted" style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-semibold)', textTransform: 'uppercase', letterSpacing: 0.3 }}>{t('cx.uploadSection')}</div>
                <div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('cx.paperHint')}</div>
                <FileDrop onFile={uploadSigned} accept="application/pdf" label={t('cx.uploadSigned')} busy={busy === 'paper'} busyLabel={t('cd.uploading')} compact />
              </div>
            </>
          )}
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 10 }}>
          {/* Estado + tracking */}
          <div style={{ display: 'grid', gap: 5, background: 'var(--surface-2)', borderRadius: 8, padding: 10 }}>
            {step(t('cx.prepared'), c.created_at, true)}
            {/* Enviado al homeowner + REENVÍOS: badge "Reenviado ×N" y la fecha de CADA envío. */}
            {sends.length === 0 ? step(t('cx.sent'), null, false) : (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 'var(--fs-sm)' }}>
                  <Dot done />
                  <span style={{ fontWeight: 'var(--fw-medium)', flex: 1 }}>
                    {t('cx.sent')}
                    {sends.length > 1 && (
                      <span className="badge" style={{ marginLeft: 8, fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)', background: 'var(--info-soft)', color: 'var(--info)' }}>
                        <RotateCcw size={10} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('cx.resentTimes', { n: String(sends.length - 1) })}
                      </span>
                    )}
                  </span>
                  <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{fmt(sends[0]!.at)}</span>
                </div>
                {sends.length > 1 && (
                  <div style={{ marginLeft: 28, marginTop: 3, display: 'grid', gap: 1 }}>
                    {sends.map((s, i) => (
                      <div key={i} className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>
                        {i === 0 ? t('cx.sentFirst') : `${t('cx.sentResend')} #${i}`}: {fmt(s.at)}{s.via === 'sms' ? ` · ${t('cx.viaSms')}` : ''}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            {step(t('cx.opened'), c.opened_at, !!c.opened_at)}
            {step(t('cx.hoSigned'), c.homeowner_signed_at, !!c.homeowner_signed_at)}
            {/* El appraiser NO tiene firma del PA: lo firma solo el homeowner. */}
            {!isAppraiser && step(t('cx.paSigned'), c.pa_signed_at, !!c.pa_signed_at)}
            {c.status === 'signed' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 'var(--fs-sm)', borderTop: '1px solid var(--line)', marginTop: 2, paddingTop: 7 }}>
                <Dot done />
                <span style={{ fontWeight: 'var(--fw-semibold)', flex: 1, color: 'var(--green)' }}>{t('cx.complete')}</span>
                {/* Link prominente al PDF firmado — el pedido: "no sé dónde verlo". Muestra el
                    contrato ejecutado (o el relleno si aún no hay firmado en R2). */}
                {(c.signed_url || c.filled_url) && (
                  <a href={(c.signed_url || c.filled_url)!} target="_blank" rel="noopener noreferrer" style={{ fontSize: 'var(--fs-xs)' }}>{t('cx.viewSigned')} ↗</a>
                )}
              </div>
            )}
          </div>
          {c.sent_to_email && <div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('cx.sentTo')}: {c.sent_to_email}</div>}

          {/* Acciones */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {/* Ver el PDF: firmado si existe, si no el relleno. Antes gateaba solo por filled_url,
                así que los contratos subidos en papel (solo signed_pdf_key) no mostraban el link. */}
            {(c.signed_url || c.filled_url) && c.status !== 'signed' && <a className="btn ghost sm" href={(c.signed_url || c.filled_url)!} target="_blank" rel="noopener noreferrer"><Eye size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('cx.view')}</a>}
            {canManage && c.status !== 'signed' && <button className="btn sm" disabled={busy === 'send'} onClick={send}><Mail size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {busy === 'send' ? '…' : (c.sent_at ? t('cx.resend') : t('cx.send'))}</button>}
            {/* Botón visible mientras NO haya firma del PA ESTAMPADA (un contrato de papel
                queda "PA firmado" por el upload sin tinta digital → todavía se puede estampar),
                y SOLO para el PA impreso: al resto se le dice quién tiene que firmar. */}
            {!isAppraiser && !!c.homeowner_signed_at && !c.pa_stamped && (
              c.can_pa_sign
                ? <button className="btn sm" style={{ background: 'var(--good-fill)', color: 'var(--good-fill-ink)', borderColor: 'transparent' }} onClick={() => setPaOpen(true)}><PenLine size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('cx.paSign')}</button>
                : <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}><Lock size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('cx.onlyPaSigns', { pa: c.pa_name ?? '—' })}</span>
            )}
            {c.signing_url && c.status !== 'signed' && <button className="btn ghost sm" onClick={() => { void copiarLinkDeFirma(c.signing_url!); }}><Link2 size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('cx.copyLink')}</button>}
            {canManage && c.signing_url && c.status !== 'signed' && <button className="btn ghost sm" disabled={busy === 'sms'} onClick={() => { if (window.confirm(t('cx.smsConfirm'))) sendSms(); }}><MessageSquare size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {busy === 'sms' ? '…' : t('cx.sendSms')}</button>}
            {/* Corregir los datos del documento (nombre, date of loss, póliza…) SIN romper las
                firmas: el PDF se rehace y el link del homeowner muestra el corregido. El
                historial de correcciones vive dentro del mismo modal, y es solo nuestro. */}
            {canManage && <button className="btn ghost sm" onClick={() => setEditOpen(true)}><Pencil size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('cx.editData')}</button>}
          </div>

          {/* Firma del PA (modal inline) */}
          {paOpen && (
            <div style={{ border: '1px solid var(--line)', borderRadius: 10, padding: 12, display: 'grid', gap: 8 }}>
              <div style={{ fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-semibold)' }}>{t('cx.paSignHere')}</div>
              <SignaturePad ref={padRef} />
              {paErr && (
                <div style={{ background: 'var(--red-soft)', border: '1px solid #fecaca', color: 'var(--red)', borderRadius: 8, padding: '8px 10px', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-semibold)' }}>
                  <AlertTriangle size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {paErr}
                </div>
              )}
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn sm" disabled={busy === 'pasign'} onClick={paSign} style={{ background: 'var(--good-fill)', color: 'var(--good-fill-ink)', borderColor: 'transparent' }}>{busy === 'pasign' ? '…' : t('cx.confirmSign')}</button>
                <button className="btn ghost sm" onClick={() => { setPaOpen(false); setPaErr(null); }}>{t('common.cancel')}</button>
              </div>
            </div>
          )}
        </div>
      )}

      {editOpen && c && (
        <ContractEditModal contractId={c.id} onClose={() => setEditOpen(false)} onSaved={() => { load(); onChanged?.(); }} />
      )}

      {/* Contrato en PAPEL: subir el PDF ya firmado → pasa a Contract Signed. (Solo cuando ya
          hay contrato preparado; el estado vacío ya tiene su propio uploader arriba.) */}
      {canManage && c && c.status !== 'signed' && (
        <div style={{ marginTop: 10, borderTop: '1px dashed var(--line)', paddingTop: 8 }}>
          <div className="muted" style={{ fontSize: 'var(--fs-2xs)', marginBottom: 4 }}>{t('cx.paperHint')}</div>
          <FileDrop onFile={uploadSigned} accept="application/pdf" label={t('cx.uploadSigned')} busy={busy === 'paper'} busyLabel={t('cd.uploading')} compact />
        </div>
      )}
    </div>
  );
}
